import React, { useMemo } from 'react';
import { useReadOnly } from '../../contexts/ReadOnlyContext';

/**
 * ReadOnlyWrapper - Automatically disables all child form elements when in readonly mode
 * 
 * This component recursively traverses through nested elements (divs, spans, etc.)
 * and disables all form inputs, textareas, selects, and buttons.
 * 
 * Performance optimization: Uses React.useMemo to cache processed children,
 * avoiding expensive tree traversal on every render when children/isReadOnly don't change.
 * 
 * Supported components:
 * - Native HTML: input, textarea, select, button, a (links)
 * - Skylab web components: s-select, s-input, s-button
 * - Skylab React components: SSelect, SInput, SButton
 * - Custom React components with onClick, onSubmit, onChange, change, onEdit, disabled, or readOnly props
 * - Any component with disabled or readOnly prop
 * 
 * Usage:
 *   <ReadOnlyWrapper>
 *     <div>
 *       <input type="text" />
 *       <SSelect optionsList={...} />
 *       <FileSaveAs onClick={...} />
 *       <AvaRIEInput change={...} />
 *       <div>
 *         <textarea />
 *         <select>...</select>
 *       </div>
 *     </div>
 *   </ReadOnlyWrapper>
 * 
 * With exceptions:
 *   <ReadOnlyWrapper>
 *     <div>
 *       <button excludeFromReadonly>Download</button>
 *       <button>This will be disabled</button>
 *     </div>
 *   </ReadOnlyWrapper>
 * 
 * With selective dropdown options (for SSelect with optionsList):
 *   <ReadOnlyWrapper>
 *     <div>
 *       <SSelect 
 *         optionsList={[
 *           {label: "Delete", value: "delete"},
 *           {label: "Export", value: "export"}
 *         ]}
 *         readonlyExemptValues={["export"]}
 *       />
 *     </div>
 *   </ReadOnlyWrapper>
 *   // In readonly mode: Delete will be disabled, Export will remain enabled
 * 
 * All inputs and interactive components at any nesting level will be automatically 
 * disabled when user has readonly role, except those marked with excludeFromReadonly
 * or specific dropdown values listed in readonlyExemptValues.
 */
const ReadOnlyWrapper = ({ children, className = '' }) => {
  const { isReadOnly } = useReadOnly();

  // Memoize the processed children - only reprocess when isReadOnly or children change
  // This prevents expensive recursive tree traversal on every parent re-render
  const processedChildren = useMemo(() => {
    // If not readonly, return children as-is
    if (!isReadOnly) {
      return children;
    }

    // Recursively clone children and add disabled prop to form elements
    const disableChildren = (child) => {
      // Handle null, undefined, strings, numbers
      if (!React.isValidElement(child)) {
        return child;
      }

      const childType = child.type;
      const childProps = child.props || {};

      // Check if this component should be excluded from read-only behavior
      // If excluded, return as-is without processing children
      if (childProps.excludeFromReadonly !== undefined) {
        return child;
      }

      // Check if it's a native HTML form element
      const isNativeFormElement =
        childType === 'input' ||
        childType === 'textarea' ||
        childType === 'select' ||
        childType === 'button' ||
        childType === 'a'; // links styled as buttons

      // Check for Skylab web components (s-select, s-input, etc.)
      const isSkylabWebComponent =
        typeof childType === 'string' &&
        childType.startsWith('s-') &&
        (childType.includes('select') || childType.includes('input') || childType.includes('button'));

      // Check for Skylab React components (SSelect, SInput, etc.)
      const isSkylabReactComponent =
        typeof childType === 'function' &&
        childType.name &&
        childType.name.startsWith('S') &&
        (childType.name.includes('Select') || childType.name.includes('Input') || childType.name.includes('Button'));

      // Check for SSelect specifically by its unique props (optionsList, onS-select)
      const isSSelect =
        childProps.optionsList !== undefined ||
        childProps['onS-select'] !== undefined;

      // Check for custom React components that might be interactive (functions/classes with props indicating interactivity)
      const isCustomInteractiveComponent =
        typeof childType === 'function' &&
        (childProps.onClick !== undefined ||
          childProps.onSubmit !== undefined ||
          childProps.onChange !== undefined ||
          childProps.change !== undefined ||
          childProps.onEdit !== undefined ||
          childProps.disabled !== undefined ||
          childProps.readOnly !== undefined);

      // Check if component already has disabled prop (indicating it's a form control)
      const hasDisabledProp = childProps.disabled !== undefined;

      // Check if component already has readOnly prop (indicating it's a form control)
      const hasReadOnlyProp = childProps.readOnly !== undefined;

      // If it's a form element or interactive component, disable it
      if (isNativeFormElement || isSkylabWebComponent || isSkylabReactComponent || isSSelect || isCustomInteractiveComponent || hasDisabledProp || hasReadOnlyProp) {
        const existingClassName = childProps.className || '';

        // For anchor tags, we need to prevent the onClick and add styling
        if (childType === 'a') {
          return React.cloneElement(child, {
            disabled: true,
            className: `${existingClassName} disabled`,
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
            },
            style: {
              pointerEvents: 'none',
              opacity: 0.5,
              cursor: 'not-allowed',
              ...childProps.style
            },
            children: childProps.children,
          });
        }

        // Special handling for components with optionsList (like SSelect)
        if (childProps.optionsList && Array.isArray(childProps.optionsList)) {
          const exemptValues = childProps.readonlyExemptValues || [];

          // If there are exempt values, do partial update (disable non-exempt options)
          // Otherwise, fully disable the entire dropdown
          if (exemptValues.length > 0) {
            // Map through options and disable those not in exemptValues
            const updatedOptionsList = childProps.optionsList.map(option => {
              // If option value is in exemptValues, don't disable it
              if (exemptValues.includes(option.value)) {
                return option;
              }
              // Otherwise, mark as disabled
              return { ...option, disabled: true };
            });

            return React.cloneElement(child, {
              ...childProps,
              optionsList: updatedOptionsList,
              className: existingClassName,
            });
          }
        }

        return React.cloneElement(child, {
          disabled: true,
          className: existingClassName,
          children: childProps.children, // Preserve original children
        });
      }

      // For container elements (div, span, fieldset, etc.), recursively process children
      if (childProps.children) {
        const processedChildren = React.Children.map(childProps.children, disableChildren);

        // Clone the container with processed children
        return React.cloneElement(child, {
          ...childProps,
          children: processedChildren,
        });
      }

      // Return element as-is if no children and not a form element
      return child;
    };

    return React.Children.map(children, disableChildren);
  }, [isReadOnly, children]);

  // Return processed children without wrapper
  return <>{processedChildren}</>;
};


export default ReadOnlyWrapper;
